import { resolveCountryPhoneInfo } from './countryPhoneData';

export interface AddressSuggestion {
  id: string;
  fullAddress: string;
  label: string;
  secondary: string;
  city?: string;
  state?: string;
  postcode?: string;
  country?: string;
}

// In-memory cache to prevent repeated network queries
const suggestionsCache = new Map<string, AddressSuggestion[]>();

// Offline curated fallback addresses for instant zero-latency suggestions
const OFFLINE_COUNTRY_SUGGESTIONS: Record<string, AddressSuggestion[]> = {
  us: [
    {
      id: 'us-nyc',
      fullAddress: 'Broadway, Manhattan, New York, NY 10001, United States',
      label: 'Broadway, New York',
      secondary: 'Manhattan, NY 10001, United States',
      city: 'New York',
      state: 'New York',
      postcode: '10001',
      country: 'United States'
    },
    {
      id: 'us-la',
      fullAddress: 'Sunset Boulevard, Los Angeles, CA 90028, United States',
      label: 'Sunset Boulevard, Los Angeles',
      secondary: 'Los Angeles, CA 90028, United States',
      city: 'Los Angeles',
      state: 'California',
      postcode: '90028',
      country: 'United States'
    },
    {
      id: 'us-chi',
      fullAddress: 'Michigan Avenue, Chicago, IL 60611, United States',
      label: 'Michigan Avenue, Chicago',
      secondary: 'Chicago, IL 60611, United States',
      city: 'Chicago',
      state: 'Illinois',
      postcode: '60611',
      country: 'United States'
    },
    {
      id: 'us-hou',
      fullAddress: 'Main Street, Houston, TX 77002, United States',
      label: 'Main Street, Houston',
      secondary: 'Houston, TX 77002, United States',
      city: 'Houston',
      state: 'Texas',
      postcode: '77002',
      country: 'United States'
    },
    {
      id: 'us-sf',
      fullAddress: 'Market Street, San Francisco, CA 94103, United States',
      label: 'Market Street, San Francisco',
      secondary: 'San Francisco, CA 94103, United States',
      city: 'San Francisco',
      state: 'California',
      postcode: '94103',
      country: 'United States'
    },
    {
      id: 'us-mia',
      fullAddress: 'Ocean Drive, Miami Beach, FL 33139, United States',
      label: 'Ocean Drive, Miami Beach',
      secondary: 'Miami Beach, FL 33139, United States',
      city: 'Miami',
      state: 'Florida',
      postcode: '33139',
      country: 'United States'
    }
  ],
  gb: [
    {
      id: 'gb-lon-1',
      fullAddress: 'Oxford Street, Westminster, London, W1D 1BS, United Kingdom',
      label: 'Oxford Street, London',
      secondary: 'Westminster, London, W1D 1BS, United Kingdom',
      city: 'London',
      state: 'Greater London',
      postcode: 'W1D 1BS',
      country: 'United Kingdom'
    },
    {
      id: 'gb-lon-2',
      fullAddress: 'Baker Street, Marylebone, London, NW1 6XE, United Kingdom',
      label: 'Baker Street, London',
      secondary: 'Marylebone, London, NW1 6XE, United Kingdom',
      city: 'London',
      state: 'Greater London',
      postcode: 'NW1 6XE',
      country: 'United Kingdom'
    },
    {
      id: 'gb-man',
      fullAddress: 'Deansgate, Manchester, M3 2BW, United Kingdom',
      label: 'Deansgate, Manchester',
      secondary: 'Manchester, M3 2BW, United Kingdom',
      city: 'Manchester',
      state: 'Greater Manchester',
      postcode: 'M3 2BW',
      country: 'United Kingdom'
    },
    {
      id: 'gb-bir',
      fullAddress: 'New Street, Birmingham, B2 4NS, United Kingdom',
      label: 'New Street, Birmingham',
      secondary: 'Birmingham, B2 4NS, United Kingdom',
      city: 'Birmingham',
      state: 'West Midlands',
      postcode: 'B2 4NS',
      country: 'United Kingdom'
    }
  ],
  ca: [
    {
      id: 'ca-tor',
      fullAddress: 'Yonge Street, Toronto, ON M4Y 1Z9, Canada',
      label: 'Yonge Street, Toronto',
      secondary: 'Toronto, ON M4Y 1Z9, Canada',
      city: 'Toronto',
      state: 'Ontario',
      postcode: 'M4Y 1Z9',
      country: 'Canada'
    },
    {
      id: 'ca-van',
      fullAddress: 'Robson Street, Vancouver, BC V6E 1B5, Canada',
      label: 'Robson Street, Vancouver',
      secondary: 'Vancouver, BC V6E 1B5, Canada',
      city: 'Vancouver',
      state: 'British Columbia',
      postcode: 'V6E 1B5',
      country: 'Canada'
    },
    {
      id: 'ca-mon',
      fullAddress: 'Rue Sainte-Catherine, Montreal, QC H3B 1H7, Canada',
      label: 'Rue Sainte-Catherine, Montreal',
      secondary: 'Montreal, QC H3B 1H7, Canada',
      city: 'Montreal',
      state: 'Quebec',
      postcode: 'H3B 1H7',
      country: 'Canada'
    }
  ],
  au: [
    {
      id: 'au-syd',
      fullAddress: 'George Street, Sydney, NSW 2000, Australia',
      label: 'George Street, Sydney',
      secondary: 'Sydney, NSW 2000, Australia',
      city: 'Sydney',
      state: 'New South Wales',
      postcode: '2000',
      country: 'Australia'
    },
    {
      id: 'au-mel',
      fullAddress: 'Bourke Street, Melbourne, VIC 3000, Australia',
      label: 'Bourke Street, Melbourne',
      secondary: 'Melbourne, VIC 3000, Australia',
      city: 'Melbourne',
      state: 'Victoria',
      postcode: '3000',
      country: 'Australia'
    },
    {
      id: 'au-bri',
      fullAddress: 'Queen Street, Brisbane, QLD 4000, Australia',
      label: 'Queen Street, Brisbane',
      secondary: 'Brisbane, QLD 4000, Australia',
      city: 'Brisbane',
      state: 'Queensland',
      postcode: '4000',
      country: 'Australia'
    }
  ],
  bd: [
    {
      id: 'bd-dhk-1',
      fullAddress: 'Dhanmondi Road 27, Dhaka 1209, Bangladesh',
      label: 'Dhanmondi, Dhaka',
      secondary: 'Dhaka Division, 1209, Bangladesh',
      city: 'Dhaka',
      state: 'Dhaka Division',
      postcode: '1209',
      country: 'Bangladesh'
    },
    {
      id: 'bd-dhk-2',
      fullAddress: 'Gulshan 2 Avenue, Dhaka 1212, Bangladesh',
      label: 'Gulshan 2, Dhaka',
      secondary: 'Dhaka Division, 1212, Bangladesh',
      city: 'Dhaka',
      state: 'Dhaka Division',
      postcode: '1212',
      country: 'Bangladesh'
    },
    {
      id: 'bd-dhk-3',
      fullAddress: 'Mirpur 10 Roundabout, Dhaka 1216, Bangladesh',
      label: 'Mirpur 10, Dhaka',
      secondary: 'Dhaka Division, 1216, Bangladesh',
      city: 'Dhaka',
      state: 'Dhaka Division',
      postcode: '1216',
      country: 'Bangladesh'
    },
    {
      id: 'bd-dhk-4',
      fullAddress: 'Uttara Sector 7, Dhaka 1230, Bangladesh',
      label: 'Uttara Sector 7, Dhaka',
      secondary: 'Dhaka Division, 1230, Bangladesh',
      city: 'Dhaka',
      state: 'Dhaka Division',
      postcode: '1230',
      country: 'Bangladesh'
    },
    {
      id: 'bd-ctg',
      fullAddress: 'GEC Circle, Chittagong 4000, Bangladesh',
      label: 'GEC Circle, Chittagong',
      secondary: 'Chittagong Division, 4000, Bangladesh',
      city: 'Chittagong',
      state: 'Chittagong Division',
      postcode: '4000',
      country: 'Bangladesh'
    },
    {
      id: 'bd-syl',
      fullAddress: 'Zindabazar, Sylhet 3100, Bangladesh',
      label: 'Zindabazar, Sylhet',
      secondary: 'Sylhet Division, 3100, Bangladesh',
      city: 'Sylhet',
      state: 'Sylhet Division',
      postcode: '3100',
      country: 'Bangladesh'
    }
  ],
  in: [
    {
      id: 'in-del',
      fullAddress: 'Connaught Place, New Delhi, Delhi 110001, India',
      label: 'Connaught Place, New Delhi',
      secondary: 'New Delhi, Delhi 110001, India',
      city: 'New Delhi',
      state: 'Delhi',
      postcode: '110001',
      country: 'India'
    },
    {
      id: 'in-mum',
      fullAddress: 'Marine Drive, Nariman Point, Mumbai, Maharashtra 400021, India',
      label: 'Marine Drive, Mumbai',
      secondary: 'Mumbai, Maharashtra 400021, India',
      city: 'Mumbai',
      state: 'Maharashtra',
      postcode: '400021',
      country: 'India'
    },
    {
      id: 'in-blr',
      fullAddress: 'MG Road, Bengaluru, Karnataka 560001, India',
      label: 'MG Road, Bengaluru',
      secondary: 'Bengaluru, Karnataka 560001, India',
      city: 'Bengaluru',
      state: 'Karnataka',
      postcode: '560001',
      country: 'India'
    },
    {
      id: 'in-kol',
      fullAddress: 'Park Street, Kolkata, West Bengal 700016, India',
      label: 'Park Street, Kolkata',
      secondary: 'Kolkata, West Bengal 700016, India',
      city: 'Kolkata',
      state: 'West Bengal',
      postcode: '700016',
      country: 'India'
    }
  ],
  ae: [
    {
      id: 'ae-dxb-1',
      fullAddress: 'Sheikh Zayed Road, Downtown Dubai, Dubai, United Arab Emirates',
      label: 'Sheikh Zayed Road, Downtown',
      secondary: 'Downtown Dubai, Dubai, UAE',
      city: 'Dubai',
      state: 'Dubai',
      country: 'United Arab Emirates'
    },
    {
      id: 'ae-dxb-2',
      fullAddress: 'Dubai Marina Walk, Dubai Marina, Dubai, United Arab Emirates',
      label: 'Dubai Marina Walk',
      secondary: 'Dubai Marina, Dubai, UAE',
      city: 'Dubai',
      state: 'Dubai',
      country: 'United Arab Emirates'
    },
    {
      id: 'ae-auh',
      fullAddress: 'Corniche Road, Abu Dhabi, United Arab Emirates',
      label: 'Corniche Road, Abu Dhabi',
      secondary: 'Abu Dhabi, UAE',
      city: 'Abu Dhabi',
      state: 'Abu Dhabi',
      country: 'United Arab Emirates'
    }
  ]
};

/**
 * Searches real matching full addresses scoped to the customer's selected country
 * Uses Photon Komoot API with Nominatim and curated local fallback
 * Fully independent, fast, no Google Maps widgets or keys needed.
 */
export async function searchAddressSuggestions(
  query: string,
  countryStr: string,
  signal?: AbortSignal
): Promise<AddressSuggestion[]> {
  const trimmed = query.trim();
  if (!trimmed || trimmed.length < 2) return [];

  const countryInfo = resolveCountryPhoneInfo(countryStr);
  const iso2 = countryInfo.iso2;
  const countryName = countryInfo.name;

  const cacheKey = `${iso2}:${trimmed.toLowerCase()}`;
  if (suggestionsCache.has(cacheKey)) {
    return suggestionsCache.get(cacheKey)!;
  }

  const results: AddressSuggestion[] = [];

  // 1. Try Photon Geocoding API
  try {
    const photonQuery = `${trimmed} ${countryName !== 'Global / Other' ? countryName : ''}`.trim();
    const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(photonQuery)}&limit=8`;
    
    const res = await fetch(url, { signal });
    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data.features)) {
        for (const feat of data.features) {
          const props = feat.properties || {};
          const cCode = (props.countrycode || '').toLowerCase();
          
          // If a specific country is selected, prefer/filter matches from that country
          if (iso2 && cCode && cCode !== iso2) {
            continue;
          }

          const parts: string[] = [];
          if (props.housenumber) parts.push(props.housenumber);
          if (props.street) parts.push(props.street);
          else if (props.name) parts.push(props.name);

          const mainLine = parts.join(' ') || props.name || trimmed;
          const locParts: string[] = [];
          if (props.city || props.town || props.village) locParts.push(props.city || props.town || props.village);
          if (props.state) locParts.push(props.state);
          if (props.postcode) locParts.push(props.postcode);
          if (props.country) locParts.push(props.country);

          const secondary = locParts.join(', ');
          const fullAddress = [mainLine, secondary].filter(Boolean).join(', ');

          results.push({
            id: `photon-${props.osm_id || Math.random()}`,
            fullAddress,
            label: mainLine,
            secondary,
            city: props.city || props.town || props.village || '',
            state: props.state || '',
            postcode: props.postcode || '',
            country: props.country || countryName
          });

          if (results.length >= 6) break;
        }
      }
    }
  } catch (err: any) {
    if (err.name === 'AbortError') throw err;
    // Network or rate limit, fall through to Nominatim or offline
  }

  // 2. Fallback to Nominatim if results are still sparse (< 3)
  if (results.length < 3) {
    try {
      let nomUrl = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(trimmed)}&format=json&addressdetails=1&limit=6`;
      if (iso2) {
        nomUrl += `&countrycodes=${encodeURIComponent(iso2)}`;
      }

      const nomRes = await fetch(nomUrl, {
        signal,
        headers: { 'Accept-Language': 'en' }
      });

      if (nomRes.ok) {
        const nomData = await nomRes.json();
        if (Array.isArray(nomData)) {
          for (const item of nomData) {
            const addr = item.address || {};
            const street = addr.road || addr.pedestrian || addr.suburb || item.name || '';
            const city = addr.city || addr.town || addr.municipality || addr.county || '';
            const state = addr.state || '';
            const postcode = addr.postcode || '';
            const country = addr.country || countryName;

            const label = street || city || item.display_name.split(',')[0];
            const secondary = [city, state, postcode, country].filter(Boolean).join(', ');
            const fullAddress = item.display_name || `${label}, ${secondary}`;

            // Avoid exact duplicates
            if (!results.some(r => r.fullAddress.toLowerCase() === fullAddress.toLowerCase())) {
              results.push({
                id: `nom-${item.place_id || Math.random()}`,
                fullAddress,
                label,
                secondary,
                city,
                state,
                postcode,
                country
              });
            }

            if (results.length >= 6) break;
          }
        }
      }
    } catch (err: any) {
      if (err.name === 'AbortError') throw err;
      // Continue to offline fallback
    }
  }

  // 3. Fallback to Curated Offline Dataset if search matches any popular local spots
  const offlinePool = OFFLINE_COUNTRY_SUGGESTIONS[iso2] || [];
  const lowerQuery = trimmed.toLowerCase();
  for (const item of offlinePool) {
    if (
      item.fullAddress.toLowerCase().includes(lowerQuery) ||
      item.label.toLowerCase().includes(lowerQuery) ||
      (item.city && item.city.toLowerCase().includes(lowerQuery))
    ) {
      if (!results.some(r => r.fullAddress.toLowerCase() === item.fullAddress.toLowerCase())) {
        results.push(item);
      }
    }
  }

  // If query is short or general and no network results returned, show top country addresses as guidance
  if (results.length === 0 && offlinePool.length > 0) {
    results.push(...offlinePool.slice(0, 3));
  }

  suggestionsCache.set(cacheKey, results);
  return results;
}
